import React, { useState } from 'react';
import PageLayout from '../components/PageLayout';
import { MapPin, DollarSign, Briefcase, ChevronDown, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { jobListings } from '../data/jobsData';
import SEOHead from '../components/SEOHead';

const departmentFilters = ['All', 'Software development', 'System development', 'Back office'];
const levelFilters = ['All', 'Internship', 'Employee', 'Leader'];

export default function JobsPage() {
  const [activeDept, setActiveDept] = useState('All');
  const [activeLevel, setActiveLevel] = useState('All');
  const navigate = useNavigate();

  const filtered = jobListings.filter(j => {
    const deptMatch = activeDept === 'All' || j.department === activeDept;
    const levelMatch = activeLevel === 'All' || j.type === activeLevel;
    return deptMatch && levelMatch;
  });

  return (
    <PageLayout>
      <SEOHead
        title="Careers & Job Openings"
        description="Join the DISION Tech team. We're hiring talented individuals passionate about cybersecurity, software development, and innovative technology solutions."
        keywords="careers, job openings, software developer jobs, cybersecurity jobs, IT jobs, tech careers, Vietnam jobs"
        url="/jobs"
      />
      <style>{`
        @media (max-width: 768px) {
          .jobs-filter-sidebar {
            display: none !important;
          }
          .jobs-main-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
      {/* Page Hero */}
      <div style={{ background: '#F9FAFB', padding: '60px 24px 50px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h1 style={{ fontSize: 'clamp(32px, 4vw, 52px)', fontWeight: '800', color: '#111827', marginBottom: '16px', letterSpacing: '-1px', lineHeight: 1.15 }}>
            Available positions
          </h1>
          <p style={{ fontSize: '18px', color: '#6B7280', maxWidth: '540px', lineHeight: 1.7 }}>
            We are looking for talented individuals to join our team. If you are passionate about cybersecurity and software development, we encourage you to apply.
          </p>
        </div>
      </div>

      {/* Main content */}
      <section style={{ padding: '60px 24px 80px', background: 'white' }}>
        <div className="jobs-main-grid" style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: '260px 1fr', gap: '40px', alignItems: 'start' }}>
          {/* Filters sidebar */}
          <div className="jobs-filter-sidebar" style={{ position: 'sticky', top: '84px' }}>
            <div style={{ background: '#F9FAFB', borderRadius: '16px', padding: '28px', border: '1px solid #E5E7EB' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#111827', marginBottom: '20px' }}>Filter positions</h3>

              <div style={{ marginBottom: '24px' }}>
                <p style={{ fontSize: '13px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '12px' }}>Level</p>
                {levelFilters.map(level => (
                  <label key={level} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="level"
                      checked={activeLevel === level}
                      onChange={() => setActiveLevel(level)}
                      style={{ accentColor: '#0099CC', width: '16px', height: '16px' }}
                    />
                    <span style={{ fontSize: '14px', color: activeLevel === level ? '#0099CC' : '#374151', fontWeight: activeLevel === level ? '600' : '400' }}>{level}</span>
                  </label>
                ))}
              </div>

              <div>
                <p style={{ fontSize: '13px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '12px' }}>Department</p>
                {departmentFilters.map(dept => (
                  <label key={dept} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="dept"
                      checked={activeDept === dept}
                      onChange={() => setActiveDept(dept)}
                      style={{ accentColor: '#0099CC', width: '16px', height: '16px' }}
                    />
                    <span style={{ fontSize: '14px', color: activeDept === dept ? '#0099CC' : '#374151', fontWeight: activeDept === dept ? '600' : '400' }}>{dept}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Job listings */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 24px', color: '#9CA3AF' }}>
                <p style={{ fontSize: '18px', fontWeight: '500' }}>No positions found matching your filters.</p>
              </div>
            ) : filtered.map((job, i) => (
              <div
                key={i}
                className="service-card"
                onClick={() => navigate(`/jobs/${job.slug}`)}
                style={{ background: '#F9FAFB', borderRadius: '16px', border: '1px solid #E5E7EB', overflow: 'hidden', cursor: 'pointer', padding: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
                    <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#111827', margin: 0 }}>{job.title}</h3>
                    {job.tag && <span style={{ backgroundColor: '#FEF2F2', color: '#DC2626', padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '700' }}>{job.tag}</span>}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <MapPin size={14} color="#6B7280" />
                      <span style={{ fontSize: '13px', color: '#6B7280' }}>{job.location}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Briefcase size={14} color="#6B7280" />
                      <span style={{ fontSize: '13px', color: '#6B7280' }}>{job.type}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <DollarSign size={14} color="#6B7280" />
                      <span style={{ fontSize: '13px', color: '#6B7280' }}>{job.salary}</span>
                    </div>
                  </div>
                  <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: 1.6, margin: 0 }}>
                    {job.about.substring(0, 140)}...
                  </p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '16px', flexShrink: 0 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#0099CC', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                    View details <ArrowRight size={14} />
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
